// @ts-strict-ignore
import React, { useState } from 'react';
import {
  Box, ButtonSize, ButtonVariant, IconName,
} from '@usealloy/component-library';
import {
  StyledNoResultsText,
  StyledSearchBoxContainer,
  StyledSearchInput,
  StyledResultsText,
  StyledSearchBoxControlContainer,
  StyledSearchBoxControlButton,
  StyledSearchIcon,
  StyledRelativeContainer,
  StyledAbsoluteContainer,
} from './styles';
import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';
import {
  selectIsSearchBoxVisible,
  selectNodeAtSearchMatchIndex,
  selectNodesMatchingSearch,
  selectPreviewAction,
  selectSearchMatchIndex,
  selectSearchQuery,
} from 'flow/store/redux/selectors';
import { setFocusedNodeId } from 'flow/store/redux/slice';
import {
  closeSearchBoxThunk, exitLinkedChildActionThunk, setSearchQueryThunk, updateSearchMatchIndexThunk,
} from 'flow/store/redux/thunks';
import { useKeyboardKeyEvent } from '../../WorkflowCanvas/hooks';
import { isPrimaryModifierPressed } from '../helpers';
import { useEditorAccess } from 'flow/editor/context';
import { checkIfEditorModeIsEditable } from 'flow/service/general';

export default function SearchBox({
  withoutContainer = false,
}: {
  withoutContainer?: boolean;
}) {
  const dispatch = useAppDispatch();

  const isSearchBoxVisible = useAppSelector(selectIsSearchBoxVisible);
  const matchedNodes = useAppSelector(selectNodesMatchingSearch);
  const searchQuery = useAppSelector(selectSearchQuery);

  const [isInputFocused, setIsInputFocused] = useState(false);

  const hasSearchQuery = Boolean(searchQuery);
  const hasSearchMatch = matchedNodes.length > 0;

  const handleSearchInputChange: React.ChangeEventHandler<HTMLInputElement> = event => {
    dispatch(setSearchQueryThunk({ searchQuery: event.target.value }));
  };

  const handleCloseClick = () => {
    dispatch(closeSearchBoxThunk());
  };

  if (!isSearchBoxVisible) {
    return null;
  }

  const content = (
    <StyledSearchBoxContainer>
      <Box>
        <StyledSearchIcon icon={IconName.Search} />
        <StyledSearchInput
          autoFocus
          type="text"
          value={searchQuery}
          onChange={handleSearchInputChange}
          placeholder="Search..."
          onFocus={() => setIsInputFocused(true)}
          onBlur={() => setIsInputFocused(false)}
        />

        {/*  flattened mutually exclusive conditions */}
        {hasSearchQuery && !hasSearchMatch && <NoResultsMessage />}
        {hasSearchQuery && hasSearchMatch && (
          <ResultsControls inputFocused={isInputFocused} />
        )}

        <StyledSearchBoxControlButton
          variant={ButtonVariant.tertiary}
          size={ButtonSize.medium}
          onClick={handleCloseClick}
          icon={IconName.Xmark}
        />
      </Box>
    </StyledSearchBoxContainer>
  );

  return (
    <StyledRelativeContainer>
      {withoutContainer ? content : <StyledAbsoluteContainer>{content}</StyledAbsoluteContainer>}
    </StyledRelativeContainer>
  );
}

interface ResultsControlsProps {
  inputFocused: boolean;
}
function ResultsControls({ inputFocused }: ResultsControlsProps) {
  const dispatch = useAppDispatch();

  const { editorMode } = useEditorAccess();
  const nodesMatchingSearch = useAppSelector(selectNodesMatchingSearch);
  const searchMatchIndex = useAppSelector(selectSearchMatchIndex);
  const nodeAtSearchMatchIndex = useAppSelector(selectNodeAtSearchMatchIndex);
  const previewActionBeingBuilt = useAppSelector(selectPreviewAction);

  const inEditMode = checkIfEditorModeIsEditable(editorMode);

  const setPrevSearchMatchIndex = () => (
    dispatch(updateSearchMatchIndexThunk({ direction: 'prev' }))
  );
  const setNextSearchMatchIndex = () => (
    dispatch(updateSearchMatchIndexThunk({ direction: 'next' }))
  );

  useKeyboardKeyEvent('Enter', event => {
    if (inEditMode && isPrimaryModifierPressed(event)) {
      if (previewActionBeingBuilt) {
        dispatch(exitLinkedChildActionThunk(previewActionBeingBuilt.publicId));
      }
      dispatch(setFocusedNodeId(nodeAtSearchMatchIndex.id));
      return;
    }

    // move towards 'next' and 'prev' on keyboard Enter / Shift + Enter
    const setter = event.shiftKey
      ? setPrevSearchMatchIndex
      : setNextSearchMatchIndex;
    setter();
  });

  useKeyboardKeyEvent('ArrowDown', () => {
    if (inputFocused) {
      setNextSearchMatchIndex();
    }
  });
  useKeyboardKeyEvent('ArrowUp', () => {
    if (inputFocused) {
      setPrevSearchMatchIndex();
    }
  });

  return (
    <StyledSearchBoxControlContainer gap={2}>
      <StyledResultsText>{searchMatchIndex + 1} of {nodesMatchingSearch.length}</StyledResultsText>
      <div>
        <StyledSearchBoxControlButton
          variant={ButtonVariant.tertiary}
          onClick={setPrevSearchMatchIndex}
          icon={IconName.CaretDown}
        />
        <StyledSearchBoxControlButton
          variant={ButtonVariant.tertiary}
          onClick={setNextSearchMatchIndex}
          icon={IconName.CaretUp}
        />
      </div>
    </StyledSearchBoxControlContainer>
  );
}


function NoResultsMessage() {
  return (
    <StyledNoResultsText>
      No results
    </StyledNoResultsText>
  );
}
